import { Injectable } from '@angular/core';

// RxJS
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';

// Services
import { NetworkService } from '@modules/core/services/network.service';
import { StateService } from '@modules/settings/services/state.service';
import { NotificationService } from '@modules/elements/services/notification.service';

// Types
import { Project } from '../types/project';
import { ServerTemplate } from '../types/server-template';
import { TaskTemplate } from '../types/task-template';

// Decorators
import { warmUpObservable } from '@decorators';
import { C3Station } from '../types/c3-station';


@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  private projects = new BehaviorSubject<Project[]>([]);
  private projectsLoaded = new BehaviorSubject<boolean>(false);
  private currentProjectId?: string;

  /**
   * Constructor
   */

  constructor(
    private stateService: StateService,
    private networkService: NetworkService,
    private notificationService: NotificationService,
  ) {
    this.stateService.getState().subscribe(state => this.currentProjectId = state.currentProject);
    this.requestProjects();
  }

  /**
   * Projects
   */

  requestProjects(): void {
    this.networkService.getProjects()
      .pipe(catchError(() => of([] as Project[])))
      .subscribe(projects => {
        this.projects.next(projects);
        this.projectsLoaded.next(true);
      });
  }

  /**
   * Emits true once the first project request has settled. ProjectGuard waits on this to tell
   * "still loading" apart from "this user has no projects" — `projects` starts as an empty array.
   */
  getProjectsLoaded(): Observable<boolean> {
    return this.projectsLoaded.asObservable();
  }

  getProjects(): Observable<Project[]> {
    return this.projects.asObservable();
  }

  getProject(id: string): Observable<Project | undefined> {
    return this.getProjects()
      .pipe(
        map(projects => projects.find(project => project?.id === id))
      )
  }

  createProject(project: Project): void {
    if (project.name) {
      this.networkService.createProject(project.name).subscribe(project => this.requestProjects())
    }
  }

  updateProject(project: Project): void {
    if (project.id && project.name) {
      this.networkService.updateProject(project.id, project).subscribe(project => this.requestProjects())
    }
  }

  @warmUpObservable
  deleteProject(id: string): Observable<any> {
    return this.networkService.deleteProject(id)
      .pipe(
        tap(resporse => this.notificationService.success(`Project deleted successfully`, '', true)),
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Project Delete: ', message);
          }
          return of();
        }),
        tap(() => this.requestProjects())
      )
  }

  /**
   * Current project
   */

  getCurrentProject(): Observable<Project> {
    return this.getProjects()
      .pipe(
        map(projects => {
          let project = projects.find(project => project?.id === this.currentProjectId) || projects[0] || undefined;
          console.log('[Current Project] ', project);
          return project;
        })
      )
  }

  get currentProject(): string | undefined {
    return this.currentProjectId;
  }

  setCurrentProject(id: string): void {
    // Re-emitting `projects` makes every getCurrentProject() subscriber re-run its handler —
    // including project-details, which PATCHes the project. ProjectGuard calls this on every
    // navigation, so an unchanged id must do nothing at all.
    if (this.currentProjectId === id) { return; }
    this.currentProjectId = id;
    this.stateService.updateState({currentProject: this.currentProjectId});
    const projects = this.projects.value;
    this.projects.next(projects);
  }

  /**
   * Server Templates
   */

  createServerTemplates(projectId: string, server: ServerTemplate): Observable<any> {
    return this.networkService.createServerTemplates(projectId, server)
      .pipe(
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Create Server: ', message);
          }
          return of();
        }),
        tap(() => this.requestProjects())
      );
  }

  getServerTemplates(projectId: string): Observable<ServerTemplate[]> {
    return this.networkService.getServerTemplates(projectId);
  }

  @warmUpObservable
  updateServerTemplates(projectId: string, server: ServerTemplate): Observable<any> {
    return this.networkService.updateServerTemplates(projectId, server)
      .pipe(
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Update Server: ', message);
          }
          return of();
        }),
        tap(() => this.requestProjects())
      );
  }

  @warmUpObservable
  deleteServerTemplates(projectId: string, serverTemplateIds: string[]): Observable<any> {
    return this.networkService.deleteServerTemplates(projectId, serverTemplateIds)
      .pipe(
        catchError(error => {
          if (error) {
            const message = ((error.error.message instanceof Array) ? error.error.message.join('\n') : error.error.message);
            this.notificationService.error('Delete Server: ', message);
          }
          return of();
        }),
        tap(() => this.requestProjects())
      );
  }

  /**
   * Task Templates
   */

  @warmUpObservable
  createTaskTemplates(serverTemplateId: string, tasks: TaskTemplate[]): Observable<any> {
    return this.networkService.createTaskTemplates(serverTemplateId, tasks)
      .pipe(tap(() => this.requestProjects()));
  }

  @warmUpObservable
  updateTaskTemplates(serverTemplateId: string, tasks: TaskTemplate[]): Observable<any> {
    return this.networkService.updateTaskTemplates(serverTemplateId, tasks)
      .pipe(tap(() => this.requestProjects()));
  }

  /**
   * C3 Stations
   */

  @warmUpObservable
  createStations(stations: {stationName: string, stationId: string}[]): Observable<any> {
    return this.networkService.createStations(stations);
  }

  getStations(): Observable<C3Station[]> {
    return this.networkService.getStations();
  }

  getStationByName(stationName: string): Observable<C3Station> {
    return this.networkService.getStationByName(stationName);
  }

  @warmUpObservable
  updateStation(station: C3Station): Observable<C3Station> {
    return this.networkService.updateStation(station);
  }

  @warmUpObservable
  deleteStation(id: string): Observable<any> {
    return this.networkService.deleteStation(id);
  }

}
